import React, { useEffect, useState } from "react";

type SendWhatsAppConfirmModalProps = {
  open: boolean;
  title?: string;
  description?: string;
  initialPhone?: string;
  sending?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (phone: string) => void | Promise<void>;
  onCancel: () => void;
};

const digitsOnlyPhone = (value: string) => value.replace(/\D/g, "").slice(0, 10);

const SendWhatsAppConfirmModal: React.FC<SendWhatsAppConfirmModalProps> = ({
  open,
  title = "Send WhatsApp message?",
  description = "Confirm the patient's WhatsApp number below, or change it before sending.",
  initialPhone = "",
  sending = false,
  confirmLabel = "Yes, send WhatsApp",
  cancelLabel = "No",
  onConfirm,
  onCancel,
}) => {
  const [phone, setPhone] = useState(digitsOnlyPhone(initialPhone));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPhone(digitsOnlyPhone(initialPhone));
      setError(null);
    }
  }, [open, initialPhone]);

  if (!open) return null;

  const handleConfirm = () => {
    const normalized = digitsOnlyPhone(phone);
    if (normalized.length !== 10) {
      setError("Enter a valid 10-digit WhatsApp number");
      return;
    }
    setError(null);
    onConfirm(normalized);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-1 items-center justify-center overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsapp-confirm-title"
    >
      <div className="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          aria-hidden="true"
        />
        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>
        <div className="inline-block px-4 pt-5 pb-4 overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          <div>
            <h3
              className="text-lg font-medium leading-6 text-gray-900"
              id="whatsapp-confirm-title"
            >
              {title}
            </h3>
            <p className="mt-2 text-sm text-gray-500">{description}</p>
            <div className="mt-4">
              <label
                htmlFor="whatsapp-confirm-phone"
                className="block text-sm font-medium text-gray-700"
              >
                WhatsApp number
              </label>
              <div className="relative mt-1 rounded-md shadow-sm">
                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-sm text-gray-500">
                  +91
                </span>
                <input
                  id="whatsapp-confirm-phone"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={phone}
                  onChange={(e) => {
                    setPhone(digitsOnlyPhone(e.target.value));
                    setError(null);
                  }}
                  disabled={sending}
                  placeholder="10-digit mobile"
                  className="block w-full rounded-md border-0 py-1.5 pl-12 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                />
              </div>
              {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
            </div>
          </div>
          <div className="mt-5 flex gap-3 sm:mt-6">
            <button
              type="button"
              disabled={sending}
              onClick={handleConfirm}
              className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 sm:text-sm"
            >
              {sending ? "Sending…" : confirmLabel}
            </button>
            <button
              type="button"
              disabled={sending}
              onClick={onCancel}
              className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 sm:text-sm"
            >
              {cancelLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendWhatsAppConfirmModal;
export { digitsOnlyPhone };
